import type {
  NetworkMapElement,
  NetworkRiskTableElement,
  PatternCardElement,
} from '../../../demo-elements/types'
import type { DemoChatScript } from '../../../types'

// Real lat/long for each facility. Projected through d3-geo's Albers USA
// inside NetworkMap, so dots land on the correct city.
const FACILITY_COORDS: Record<string, { longitude: number; latitude: number }> =
  {
    Atlanta: { longitude: -84.388, latitude: 33.749 },
    Scottsdale: { longitude: -111.926, latitude: 33.494 },
    Phoenix: { longitude: -112.074, latitude: 33.448 },
    Austin: { longitude: -97.743, latitude: 30.267 },
    Sacramento: { longitude: -121.494, latitude: 38.582 },
    'Lorain County': { longitude: -82.182, latitude: 41.452 },
    Houston: { longitude: -95.37, latitude: 29.76 },
    'New Orleans': { longitude: -90.071, latitude: 29.951 },
    Tampa: { longitude: -82.458, latitude: 27.951 },
    Birmingham: { longitude: -86.802, latitude: 33.521 },
    Memphis: { longitude: -90.049, latitude: 35.149 },
    Nashville: { longitude: -86.781, latitude: 36.162 },
    Charlotte: { longitude: -80.843, latitude: 35.227 },
    Raleigh: { longitude: -78.638, latitude: 35.779 },
    Jacksonville: { longitude: -81.656, latitude: 30.332 },
    Orlando: { longitude: -81.379, latitude: 28.538 },
    Miami: { longitude: -80.192, latitude: 25.762 },
    Conover: { longitude: -81.218, latitude: 35.708 },
    Boston: { longitude: -71.058, latitude: 42.36 },
    Newark: { longitude: -74.172, latitude: 40.735 },
    Detroit: { longitude: -83.046, latitude: 42.331 },
    Chicago: { longitude: -87.629, latitude: 41.878 },
    Denver: { longitude: -104.991, latitude: 39.738 },
    'Salt Lake City': { longitude: -111.891, latitude: 40.76 },
    Portland: { longitude: -122.679, latitude: 45.515 },
    Seattle: { longitude: -122.332, latitude: 47.606 },
  }

function coords(name: string): { longitude: number; latitude: number } {
  return FACILITY_COORDS[name] ?? { longitude: -98, latitude: 39 }
}

const NETWORK_MAP_ID = 'map-network'

const BASE_GREEN_FACILITIES = [
  'Memphis',
  'Nashville',
  'Charlotte',
  'Raleigh',
  'Jacksonville',
  'Orlando',
  'Miami',
  'Conover',
  'Boston',
  'Newark',
  'Detroit',
  'Chicago',
  'Denver',
  'Salt Lake City',
  'Portland',
  'Seattle',
  'Birmingham',
] as const

const NETWORK_MAP_BEAT_1: NetworkMapElement = {
  kind: 'network-map',
  id: NETWORK_MAP_ID,
  facilities: [
    {
      name: 'Atlanta',
      status: 'red',
      emphasize: true,
      badge: '$148K',
      ...coords('Atlanta'),
    },
    {
      name: 'Scottsdale',
      status: 'red',
      emphasize: true,
      badge: '$96K',
      ...coords('Scottsdale'),
    },
    {
      name: 'Phoenix',
      status: 'red',
      emphasize: true,
      badge: '$89K',
      ...coords('Phoenix'),
    },
    {
      name: 'Austin',
      status: 'orange',
      emphasize: true,
      badge: '$72K',
      ...coords('Austin'),
    },
    {
      name: 'Sacramento',
      status: 'orange',
      emphasize: true,
      badge: '$68K',
      ...coords('Sacramento'),
    },
    {
      name: 'Lorain County',
      status: 'orange',
      emphasize: true,
      badge: '$54K',
      ...coords('Lorain County'),
    },
    {
      name: 'Houston',
      status: 'yellow',
      emphasize: true,
      badge: '$38K',
      ...coords('Houston'),
    },
    {
      name: 'New Orleans',
      status: 'yellow',
      emphasize: true,
      badge: '$34K',
      ...coords('New Orleans'),
    },
    {
      name: 'Tampa',
      status: 'yellow',
      emphasize: true,
      badge: '$31K',
      ...coords('Tampa'),
    },
    ...BASE_GREEN_FACILITIES.map((name) => ({
      name,
      status: 'green' as const,
      ...coords(name),
    })),
  ],
}

const NETWORK_MAP_HYSPEC: NetworkMapElement = {
  kind: 'network-map',
  id: NETWORK_MAP_ID,
  patternLabel: 'Machinex Hyspec lot MX-2022-Q4-B',
  facilities: NETWORK_MAP_BEAT_1.facilities.map((f) => {
    const isHyspec = ['Scottsdale', 'Phoenix', 'Austin', 'Sacramento'].includes(
      f.name,
    )
    if (isHyspec) {
      const isFailed = ['Scottsdale', 'Phoenix'].includes(f.name)
      return {
        ...f,
        status: isFailed ? ('red' as const) : ('orange' as const),
        emphasize: true,
        badge: isFailed ? 'Failed' : 'At risk',
      }
    }
    return {
      ...f,
      status: 'green' as const,
      emphasize: false,
      badge: undefined,
    }
  }),
  patternLines: [
    { fromName: 'Scottsdale', toName: 'Phoenix', color: '#2563eb' },
    { fromName: 'Phoenix', toName: 'Austin', color: '#2563eb' },
    { fromName: 'Austin', toName: 'Sacramento', color: '#2563eb' },
    { fromName: 'Sacramento', toName: 'Scottsdale', color: '#2563eb' },
  ],
}

const NETWORK_MAP_GULF: NetworkMapElement = {
  kind: 'network-map',
  id: NETWORK_MAP_ID,
  patternLabel: 'Gulf Coast humidity ramp',
  facilities: NETWORK_MAP_BEAT_1.facilities.map((f) => {
    const isGulf = ['Houston', 'New Orleans', 'Tampa'].includes(f.name)
    if (isGulf) {
      return {
        ...f,
        status: 'yellow' as const,
        emphasize: true,
        badge: '+15% w/w',
      }
    }
    return {
      ...f,
      status: 'green' as const,
      emphasize: false,
      badge: undefined,
    }
  }),
  patternLines: [
    { fromName: 'Houston', toName: 'New Orleans', color: '#2563eb' },
    { fromName: 'New Orleans', toName: 'Tampa', color: '#2563eb' },
  ],
}

const NETWORK_RISK_TABLE: NetworkRiskTableElement = {
  kind: 'network-risk-table',
  id: 'network-risk-table',
  subtitle: '47 MRFs + 2 Polymer Centers · this morning',
  rows: [
    {
      rank: 1,
      facility: 'Atlanta',
      status: 'red',
      exposureDollars: 148_000,
      cause: 'Primary Baler #2 hydraulic seal (Act 2)',
    },
    {
      rank: 2,
      facility: 'Scottsdale',
      status: 'red',
      exposureDollars: 96_000,
      cause: 'Hyspec sorter down — failed Apr 5',
      cluster: 'hyspec',
    },
    {
      rank: 3,
      facility: 'Phoenix',
      status: 'red',
      exposureDollars: 89_000,
      cause: 'Hyspec sorter down — failed Apr 27',
      cluster: 'hyspec',
    },
    {
      rank: 4,
      facility: 'Austin',
      status: 'orange',
      exposureDollars: 72_000,
      cause: 'Hyspec sorter — failure inside 60 days',
      cluster: 'hyspec',
    },
    {
      rank: 5,
      facility: 'Sacramento',
      status: 'orange',
      exposureDollars: 68_000,
      cause: 'Hyspec sorter — failure inside 60 days',
      cluster: 'hyspec',
    },
    {
      rank: 6,
      facility: 'Lorain County',
      status: 'orange',
      exposureDollars: 54_000,
      cause: 'Motor bearing drift on the eddy current',
    },
    {
      rank: 7,
      facility: 'Houston',
      status: 'yellow',
      exposureDollars: 38_000,
      cause: 'OCC screen humidity ramp',
      cluster: 'gulf',
    },
    {
      rank: 8,
      facility: 'New Orleans',
      status: 'yellow',
      exposureDollars: 34_000,
      cause: 'OCC screen humidity ramp',
      cluster: 'gulf',
    },
    {
      rank: 9,
      facility: 'Tampa',
      status: 'yellow',
      exposureDollars: 31_000,
      cause: 'OCC screen humidity ramp',
      cluster: 'gulf',
    },
  ],
  takeaway:
    '9 sites carry most of the next 30 days of exposure. 7 of them are in 2 patterns.',
}

const HYSPEC_PATTERN: PatternCardElement = {
  kind: 'pattern-card',
  id: 'pattern-hyspec',
  tag: 'Manufacturer lot pattern',
  title: 'Machinex Hyspec sorter — lot MX-2022-Q4-B',
  stats: [
    { label: 'Equipment', value: 'Machinex MACH Hyspec' },
    { label: 'Installed', value: 'Sep 2022 – Apr 2023' },
    { label: 'Units in network', value: '12 across 4 sites' },
    { label: 'Failures since 2024', value: '10 of 12', emphasize: true },
    { label: 'Lot failure rate', value: '83%', emphasize: true },
    { label: 'Failure mode', value: 'Ejection actuator solenoid' },
  ],
  affected: [
    {
      facility: 'Scottsdale',
      status: 'red',
      note: 'Down on Apr 5. 39 days ago.',
    },
    {
      facility: 'Phoenix',
      status: 'red',
      note: 'Down on Apr 27. 17 days ago.',
    },
    {
      facility: 'Austin',
      status: 'orange',
      note: 'Same drift signature today. Failure inside 60 days.',
    },
    {
      facility: 'Sacramento',
      status: 'orange',
      note: 'Same drift signature today. Failure inside 60 days.',
    },
  ],
  takeaway:
    '~$250K of avoidable downtime — and a warranty conversation with Machinex.',
}

const GULF_PATTERN: PatternCardElement = {
  kind: 'pattern-card',
  id: 'pattern-gulf',
  tag: 'Seasonal pattern',
  title: 'Gulf Coast OCC screens — humidity ramp',
  stats: [
    { label: 'Facilities', value: 'Houston, New Orleans, Tampa' },
    { label: 'Pattern', value: 'Humidity-driven jams' },
    { label: 'Historical signature', value: '2–3× baseline · Jun–Aug' },
    {
      label: 'Current indicator',
      value: '+15% w/w vibration',
      emphasize: true,
    },
    { label: 'Onset window', value: 'First week of June', emphasize: true },
    { label: 'History depth', value: '3 yrs · 2023, 2024, 2025' },
  ],
  affected: [
    {
      facility: 'Houston',
      status: 'yellow',
      note: 'Vibration up 15% w/w. Same shape as past summer onsets.',
    },
    {
      facility: 'New Orleans',
      status: 'yellow',
      note: 'Same signature. Same pace.',
    },
    {
      facility: 'Tampa',
      status: 'yellow',
      note: 'Same signature. Same pace.',
    },
  ],
  takeaway:
    'Cheaper to pre-position now than to react when crews are already behind in June.',
}

/**
 * Act 3 — Network-scale pattern recognition.
 *
 * Three beats joined by `await-user-prompt`:
 *   1. Network risk view — 9 sites carry ~80% of next-30-day exposure.
 *   2. Hyspec MX-2022-Q4-B lot pattern — fleet-wide warranty campaign.
 *   3. Gulf Coast humidity seasonal pattern — anticipatory regional play.
 *
 * Everything beyond a one- or two-sentence beat opener lives in structured
 * elements (network map, risk table, pattern card, chart). Narrative text
 * stays out of the way of the data.
 */
export const networkPatterns: DemoChatScript = {
  id: 'network-patterns',
  name: 'Network-scale pattern recognition',
  description:
    'Act 3. Portfolio risk, fleet-wide manufacturer-lot warranty patterns, and seasonal/regional anticipation.',
  triggers: [
    'show me the network',
    'national view',
    'all 47 facilities',
    'portfolio risk',
    'network risk',
    'network patterns',
    'fleet patterns',
  ],
  events: [
    // ─────────── Beat 1: Network risk view ───────────
    {
      type: 'status',
      delayMs: 200,
      message: 'Pulling risk scores across all 47 MRFs and the 2 PCs...',
    },
    { type: 'thinking-start', delayMs: 200 },
    {
      type: 'thinking-delta',
      delayMs: 400,
      delta:
        'Ranking by 30-day downtime exposure. Grouping by shared root cause.\n',
    },
    { type: 'thinking-stop', delayMs: 200 },
    { type: 'tool-call', delayMs: 200, name: 'query_network_risk' },
    {
      type: 'text-delta',
      delayMs: 500,
      delta:
        '47 MRFs, 2 polymer centers. Most are clean. The risk concentrates in 9 sites — and 7 of those 9 are in just two patterns.',
    },
    { type: 'demo-element-add', delayMs: 500, element: NETWORK_MAP_BEAT_1 },
    { type: 'demo-element-add', delayMs: 400, element: NETWORK_RISK_TABLE },
    {
      type: 'demo-element-add',
      delayMs: 600,
      element: {
        kind: 'text-block',
        id: 'net-beat1-closer',
        text: 'Want to dig into the **Machinex lot defect first**, or the **Gulf humidity ramp**?',
      },
    },
    { type: 'done', delayMs: 300, title: 'Network risk — top exposure' },

    // ─────────── PAUSE — demoer types anything to advance to Hyspec ───────────
    { type: 'await-user-prompt', delayMs: 0 },

    // ─────────── Beat 2: Hyspec MX-2022-Q4-B lot pattern ───────────
    {
      type: 'status',
      delayMs: 200,
      message: 'Pulling lot history across the fleet...',
    },
    { type: 'thinking-start', delayMs: 200 },
    {
      type: 'thinking-delta',
      delayMs: 400,
      delta:
        'Same vendor, same model, same lot. Checking for correlated failure modes across the 4 sites.\n',
    },
    { type: 'thinking-stop', delayMs: 200 },
    { type: 'tool-call', delayMs: 200, name: 'query_mpower_warranty' },
    { type: 'tool-call', delayMs: 400, name: 'cross_network_pattern_match' },
    {
      type: 'text-delta',
      delayMs: 500,
      delta:
        'Same lot, same failure mode. Two sites already down. Two more drifting the same way right now.',
    },
    { type: 'demo-element-add', delayMs: 400, element: NETWORK_MAP_HYSPEC },
    { type: 'demo-element-add', delayMs: 400, element: HYSPEC_PATTERN },
    {
      type: 'demo-element-add',
      delayMs: 600,
      element: {
        kind: 'text-block',
        id: 'net-beat2-closer',
        text: 'One query found it across four sites. Approve the fleet campaign — or pass.',
      },
    },
    {
      type: 'approval',
      delayMs: 500,
      proposal: {
        proposalId: 'demo-rs-act3-hyspec-campaign',
        toolName: 'create_fleet_campaign',
        actionType: 'warranty_campaign',
        description:
          'Fleet service campaign — Machinex Hyspec lot MX-2022-Q4-B. 4 facilities. Open Machinex warranty engagement; schedule preventive solenoid replacement at Austin + Sacramento.',
      },
    },
    { type: 'done', delayMs: 300, title: 'Hyspec MX-2022-Q4-B pattern' },

    // ─────────── PAUSE — demoer types anything to advance to Gulf ───────────
    { type: 'await-user-prompt', delayMs: 0 },

    // ─────────── Beat 3: Gulf Coast humidity seasonal pattern ───────────
    {
      type: 'status',
      delayMs: 200,
      message: 'Pulling 3 years of Gulf Coast jam rates...',
    },
    { type: 'thinking-start', delayMs: 200 },
    {
      type: 'thinking-delta',
      delayMs: 400,
      delta:
        'OCC screens at the humid sites have jammed 2–3× baseline every June–August for the last three summers. Checking whether this summer is starting the same way.\n',
    },
    { type: 'thinking-stop', delayMs: 200 },
    { type: 'tool-call', delayMs: 200, name: 'query_seasonal_history' },
    { type: 'tool-call', delayMs: 300, name: 'query_scada_vibration' },
    {
      type: 'text-delta',
      delayMs: 500,
      delta:
        'Different pattern, same idea. Weather, not vendor. June is two weeks out and the ramp is already starting.',
    },
    { type: 'demo-element-add', delayMs: 400, element: NETWORK_MAP_GULF },
    { type: 'demo-element-add', delayMs: 400, element: GULF_PATTERN },
    {
      type: 'chart-data',
      delayMs: 500,
      chart: {
        type: 'line',
        title: 'Gulf Coast OCC screen jam rate — last 3 summers vs. this year',
        xAxisKey: 'month',
        yAxisKeys: ['2023', '2024', '2025', '2026'],
        yAxisLabel: 'Jams per 1,000 tons',
        data: [
          { month: 'Jan', '2023': 1.1, '2024': 1.0, '2025': 1.2, '2026': 1.1 },
          { month: 'Feb', '2023': 1.0, '2024': 1.1, '2025': 1.0, '2026': 1.0 },
          { month: 'Mar', '2023': 1.2, '2024': 1.1, '2025': 1.3, '2026': 1.2 },
          { month: 'Apr', '2023': 1.4, '2024': 1.5, '2025': 1.4, '2026': 1.6 },
          { month: 'May', '2023': 1.8, '2024': 1.9, '2025': 1.8, '2026': 2.0 },
          { month: 'Jun', '2023': 2.8, '2024': 3.1, '2025': 3.0 },
          { month: 'Jul', '2023': 3.4, '2024': 3.6, '2025': 3.5 },
          { month: 'Aug', '2023': 3.1, '2024': 3.3, '2025': 3.2 },
          { month: 'Sep', '2023': 2.0, '2024': 2.1, '2025': 2.0 },
          { month: 'Oct', '2023': 1.4, '2024': 1.5, '2025': 1.4 },
          { month: 'Nov', '2023': 1.1, '2024': 1.2, '2025': 1.1 },
          { month: 'Dec', '2023': 1.0, '2024': 1.0, '2025': 1.0 },
        ],
      },
    },
    {
      type: 'demo-element-add',
      delayMs: 600,
      element: {
        kind: 'text-block',
        id: 'net-beat3-closer',
        text: 'Pre-position the parts, pre-schedule the cleaning shifts. Cheap moves — but you only make them if you see the ramp coming.',
      },
    },
    {
      type: 'approval',
      delayMs: 500,
      proposal: {
        proposalId: 'demo-rs-act3-gulf-prep',
        toolName: 'create_prepositioning',
        actionType: 'seasonal_campaign',
        description:
          'Pre-position OCC screen bearings + disc sets at Houston, New Orleans, Tampa. Pre-schedule 2 extra cleaning shifts / week, June–August.',
      },
    },
    { type: 'done', delayMs: 300, title: 'Gulf Coast humidity pattern' },
  ],
}
